<script lang="ts">
  import { pageTitle } from '../../lib/pageTitle';
  import { publicAppOrigin } from '#lib/publicUrls.ts';
</script>

<svelte:head>
  <title>{pageTitle('Terms of Service')}</title>
</svelte:head>

<div class="container clear-nav">
  <h1>Terms of Service</h1>
  <p><strong>Last updated:</strong> May 26, 2026</p>
  <p>
    Welcome to SpinFlip. These Terms of Service ("Terms") govern your access to
    and use of the website located at
    <a href={publicAppOrigin()} target="_blank" rel="noopener noreferrer"
      >{publicAppOrigin()}</a
    >, any associated mobile or web applications, and any services, features,
    or content offered by SpinFlip LLC (collectively, the "Services").
  </p>
  <p>
    Please read these Terms carefully before using our Services. By accessing
    or using any part of the Services, you agree to be bound by these Terms.
    If you do not agree to all the terms and conditions of this agreement, then
    you may not access the website or use any Services.
  </p>
  <p>
    For information about how we collect and use personal information, please
    see our <a href="/privacy">Privacy Policy</a>.
  </p>

  <h2>1. Eligibility and Account Registration</h2>
  <h3>1.1. Age Requirements</h3>
  <p>
    By agreeing to these Terms, you represent that you are at least the age of
    majority in your state or province of residence.
  </p>
  <h3>1.2. Account Security</h3>
  <p>
    To access certain features of the Services, including making purchases or
    accessing specialized application dashboards, you may be required to
    register for an account. You agree to provide accurate, current, and
    complete information during registration. You are solely responsible for
    safeguarding your account password and for any activities or actions under
    your account. You must notify us immediately of any unauthorized use of
    your account.
  </p>

  <h2>2. Intellectual Property Rights</h2>
  <h3>2.1. Ownership</h3>
  <p>
    All content, features, and functionality on the Services—including but not
    limited to text, graphics, logos, icons, images, audio clips, digital
    downloads, data compilations, software, and code—are the exclusive property
    of SpinFlip LLC or its licensors and are protected by United States and
    international copyright, trademark, patent, trade secret, and other
    intellectual property laws.
  </p>
  <h3>2.2. Limited License</h3>
  <p>
    Subject to your compliance with these Terms, SpinFlip LLC grants you a
    limited, non-exclusive, non-transferable, revocable license to access and use
    the Services for your internal business or personal use, as intended by the
    functionality of the platform.
  </p>

  <h2>3. User Conduct and Prohibited Uses</h2>
  <p>You agree not to use the Services:</p>
  <ul>
    <li>
      For any unlawful purpose or to solicit others to perform or participate in
      any unlawful acts.
    </li>
    <li>
      To infringe upon or violate our intellectual property rights or the
      intellectual property rights of others.
    </li>
    <li>
      To upload or transmit viruses, worms, malware, or any other type of
      malicious code that will or may be used in any way to affect the
      functionality or operation of the Services.
    </li>
    <li>
      To reverse engineer, decompile, or disassemble any portion of the software
      powering the Services.
    </li>
    <li>
      To engage in any automated scraping, data mining, or harvesting of data
      from the platform without explicit written permission.
    </li>
  </ul>

  <h2>4. Online Purchases and E-Commerce Terms</h2>
  <p>
    Certain aspects of the Services may allow you to purchase products or
    subscription services online.
  </p>
  <h3>4.1. Product Availability and Pricing</h3>
  <p>
    All products and services listed on the site are subject to availability. We
    reserve the right to discontinue any product or modify pricing at any time
    without prior notice. We shall not be liable to you or to any third-party
    for any modification, price change, suspension, or discontinuance of the
    Services.
  </p>
  <h3>4.2. Billing and Payment</h3>
  <p>
    You agree to provide current, complete, and accurate purchase and account
    information for all purchases made through our platform. We utilize
    third-party payment processors to handle transactions securely. By
    submitting payment information, you authorize us (via our third-party
    processors) to charge the designated payment method for the total amount of
    your purchase, including any applicable taxes and shipping fees.
  </p>
  <h3>4.3. Order Acceptance and Cancellation</h3>
  <p>
    We reserve the right to refuse or cancel any order you place with us. We
    may, in our sole discretion, limit or cancel quantities purchased per
    person, per household, or per order. In the event that we make a change to
    or cancel an order, we will attempt to notify you by contacting the email
    provided at the time the order was made.
  </p>
  <h3>4.4. Shipping and Delivery</h3>
  <p>
    Risk of loss and title for physical items purchased by you pass to you upon
    our delivery of the items to the third-party carrier.
  </p>

  <h2>5. Refund and Cancellation Policy</h2>
  <h3>5.1. E-Commerce Products</h3>
  <p>
    Except as otherwise explicitly stated on a specific product page or required
    by applicable law, all digital and physical product sales are final.
  </p>
  <h3>5.2. Subscription/SaaS Services</h3>
  <p>
    If applicable, subscription cancellations will take effect at the end of
    the current billing cycle. No refunds or credits will be provided for
    partial months of service or unused periods.
  </p>

  <h2>6. Disclaimer of Warranties</h2>
  <p>
    THE SERVICES AND ALL PRODUCTS AND SERVICES DELIVERED TO YOU THROUGH THE
    PLATFORM ARE (EXCEPT AS EXPRESSLY STATED BY US) PROVIDED 'AS IS' AND 'AS
    AVAILABLE' FOR YOUR USE, WITHOUT ANY REPRESENTATION, WARRANTIES OR CONDITIONS
    OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING ALL IMPLIED WARRANTIES OR
    CONDITIONS OF MERCHANTABILITY, MERCHANTABLE QUALITY, FITNESS FOR A
    PARTICULAR PURPOSE, DURABILITY, TITLE, AND NON-INFRINGEMENT.
  </p>

  <h2>7. Limitation of Liability</h2>
  <p>
    IN NO CASE SHALL SPINFLIP LLC, OUR DIRECTORS, OFFICERS, EMPLOYEES,
    AFFILIATES, AGENTS, CONTRACTORS, OR LICENSORS BE LIABLE FOR ANY INJURY,
    LOSS, CLAIM, OR ANY DIRECT, INDIRECT, INCIDENTAL, PUNITIVE, SPECIAL, OR
    CONSEQUENTIAL DAMAGES OF ANY KIND, INCLUDING, WITHOUT LIMITATION LOST
    PROFITS, LOST REVENUE, LOST SAVINGS, LOSS OF DATA, REPLACEMENT COSTS, OR
    ANY SIMILAR DAMAGES, WHETHER BASED IN CONTRACT, TORT (INCLUDING NEGLIGENCE),
    STRICT LIABILITY OR OTHERWISE, ARISING FROM YOUR USE OF ANY OF THE SERVICES
    OR ANY PRODUCTS PROCURED USING THE SERVICES, EVEN IF ADVISED OF THEIR
    POSSIBILITY. BECAUSE SOME STATES OR JURISDICTIONS DO NOT ALLOW THE
    EXCLUSION OR THE LIMITATION OF LIABILITY FOR CONSEQUENTIAL OR INCIDENTAL
    DAMAGES, IN SUCH STATES OR JURISDICTIONS, OUR LIABILITY SHALL BE LIMITED TO
    THE MAXIMUM EXTENT PERMITTED BY LAW.
  </p>

  <h2>8. Indemnification</h2>
  <p>
    You agree to indemnify, defend, and hold harmless SpinFlip LLC and our
    parent, subsidiaries, affiliates, partners, officers, directors, agents,
    and employees, harmless from any claim or demand, including reasonable
    attorneys' fees, made by any third-party due to or arising out of your
    breach of these Terms of Service or the documents they incorporate by
    reference, or your violation of any law or the rights of a third-party.
  </p>

  <h2>9. Severability</h2>
  <p>
    In the event that any provision of these Terms of Service is determined to
    be unlawful, void, or unenforceable, such provision shall nonetheless be
    enforceable to the fullest extent permitted by applicable law, and the
    unenforceable portion shall be deemed to be severed from these Terms of
    Service, such determination shall not affect the validity and
    predictability of any other remaining provisions.
  </p>

  <h2>10. Governing Law and Jurisdiction</h2>
  <p>
    These Terms of Service and any separate agreements whereby we provide you
    Services shall be governed by and construed in accordance with the laws of
    the State of Ohio, United States, without regard to its conflict of law
    principles. Any legal action or proceeding arising under these Terms will
    be brought exclusively in the federal or state courts located in Columbus,
    Ohio.
  </p>

  <h2>11. Changes to Terms of Service</h2>
  <p>
    We reserve the right, at our sole discretion, to update, change, or replace
    any part of these Terms of Service by posting updates and changes to our
    website. It is your responsibility to check our website periodically for
    changes. Your continued use of or access to our website or the Service
    following the posting of any changes to these Terms of Service constitutes
    acceptance of those changes.
  </p>

  <h2>12. Contact Information</h2>
  <p>Questions about the Terms of Service should be sent to us at:</p>
  <p>
    Email: <a href="mailto:info@spin-flip.com">info@spin-flip.com</a><br />
    Corporate Entity: SpinFlip LLC
  </p>
</div>

<style>
  .clear-nav {
    margin-top: 0;
    font-size: 1rem;
    line-height: 1.6;
    padding: 0 1rem;
  }

  ul {
    margin-left: 1.5rem;
  }

  ul li {
    margin-bottom: 0.75rem;
  }

  h2 {
    margin-top: 2rem;
  }

  h3 {
    margin-top: 1.5rem;
  }

  @media (max-width: 768px) {
    .clear-nav {
      font-size: 0.9rem;
    }

    ul li {
      margin-bottom: 0.6rem;
    }
  }
</style>
